import createCoreSdk, { SdkResponse } from '@descope/core-js-sdk';
import createWebAuthn from './webauthn';
import createFedCM from './fedcm';
import withFlow from './flow';
import {
  getSessionToken,
  getRefreshToken,
} from '../enhancers/withPersistTokens/helpers';
import { hasLoginIndicator } from '../enhancers/withLoggedInIndicator/helpers';
import { LOGGED_IN_INDICATOR_DISABLED_KEY } from '../enhancers/withLoggedInIndicator/constants';
import { getLocalStorage } from '../enhancers/helpers';
import createOidc from './oidc';
import { CoreSdk, WebSdkConfig } from '../types';
import {
  OIDC_LOGOUT_ERROR_CODE,
  OIDC_REFRESH_ERROR_CODE,
  REFRESH_DISABLED,
} from '../constants';
import logger from '../enhancers/helpers/logger';
import { isDescopeBridge } from '../enhancers/helpers';

const createSdk = (config: WebSdkConfig) => {
  const coreSdk = createCoreSdk(config);

  const oidc = createOidc(coreSdk, config.projectId, config.oidcConfig);

  return {
    ...coreSdk,
    refresh: async (
      token?: string,
      tryRefresh?: boolean,
    ): ReturnType<CoreSdk['refresh']> => {
      if (isDescopeBridge()) {
        logger.debug(`Refresh called in native flow: ${new Error().stack}`);
        return Promise.resolve({
          ok: false,
          error: {
            errorCode: REFRESH_DISABLED,
            errorDescription:
              'Refresh is not supported in native flows via the web SDK',
          },
        });
      }

      if (config.oidcConfig) {
        try {
          await oidc.refreshToken(token);
          return Promise.resolve({ ok: true });
        } catch (error) {
          return Promise.resolve({
            ok: false,
            error: {
              errorCode: OIDC_REFRESH_ERROR_CODE,
              errorDescription: error.toString(),
            },
          });
        }
      }

      // Skip the up-front /try-refresh round-trip when localStorage has no sign
      // of a prior authenticated session. `withLoggedInIndicator` writes DSLI
      // on every successful auth and clears it on logout / invalid session.
      // DSLI_DISABLED overrides the skip — escape hatch for apps that hit the
      // storeLastAuthenticatedUser=false edge case after upgrading.
      // OIDC mode is handled above and bypasses this optimization, since OIDC
      // sessions live under a separate `oidc-client-ts` key and `oidc.refreshToken`
      // performs its own no-session short-circuit.
      if (
        tryRefresh &&
        !hasLoginIndicator() &&
        !getLocalStorage(LOGGED_IN_INDICATOR_DISABLED_KEY)
      ) {
        return Promise.resolve({ ok: true });
      }
      // Descope use this query param to monitor if refresh is made
      // When the user is already logged in in the past or not (We want to optimize that in the future)
      const currentSessionToken = getSessionToken();
      const currentRefreshToken = getRefreshToken();

      let externalToken = '';
      if (config.getExternalToken) {
        try {
          externalToken = await config.getExternalToken?.();
        } catch (error) {
          logger.debug('Error getting external token while refreshing', error);
          // continue without external token
        }
      }

      return coreSdk.refresh(
        token,
        {
          dcs: currentSessionToken ? 't' : 'f',
          dcr: currentRefreshToken ? 't' : 'f',
        },
        externalToken,
        tryRefresh,
      );
    },
    // Call the logout function according to the oidcConfig
    // And return the response in the same format
    logout: async (token?: string): Promise<SdkResponse<never>> => {
      if (config.oidcConfig) {
        // logout is made with id_token_hint
        try {
          await oidc.logout({ id_token_hint: token });
          return Promise.resolve({ ok: true });
        } catch (error) {
          return Promise.resolve({
            ok: false,
            error: {
              errorCode: OIDC_LOGOUT_ERROR_CODE,
              errorDescription: error.toString(),
            },
          });
        }
      }
      return coreSdk.logout(token);
    },
    flow: withFlow(coreSdk),
    webauthn: createWebAuthn(coreSdk),
    fedcm: createFedCM(coreSdk, config.projectId),
    oidc,
  };
};

export default createSdk;

export type CreateWebSdk = typeof createSdk;
export type WebSdk = ReturnType<CreateWebSdk>;
